<script setup lang="ts">
import { debounce } from "es-toolkit";
import type {
  AdminAthleteResponse,
  AdminAthleteRosterResponse,
  AdminRosterResponse,
} from "mws-sports-ts-sdk";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import BaseButton from "@/common/components/BaseButton.vue";
import CardVue from "@/common/components/card/CardVue.vue";
import InRowEditField from "@/common/components/form/InRowEditField.vue";
import FormSection from "@/common/components/FormSection/FormSection.vue";
import HorizontalTabs from "@/common/components/HorizontalTabs/HorizontalTabs.vue";
import RouterLinkVue from "@/common/components/RouterLinkVue.vue";
import TableVue from "@/common/components/table/TableVue.vue";
import type { TableColumn } from "@/common/components/table/types";
import type { FormSectionDetails } from "@/common/types";
import { getErrorMessage } from "@/common/utils/getErrorMessage";
import AddAthleteToRosterModal from "@/teams/components/AddAthleteToRosterModal.vue";
import RosterAthleteActionMenu from "@/teams/components/RosterAthleteActionMenu.vue";
import {
  useApproveRosterAthleteMutation,
  useUpdateRosterAthleteMutation,
} from "@/teams/rosters/api/queries";

definePage({
  meta: {
    auth: ["Admin.Access"],
  },
});

const { roster } = defineProps<{
  roster: AdminRosterResponse;
}>();

const route = useRoute("/teams/[teamId].rosters/[rosterId]");
const rosterId = computed(() => route.params.rosterId);
const isAthleteCreationModalOpen = ref(false);
const activeTab = ref<"active" | "pending" | "archived">("active");
const toast = useToast();

const detailsList = computed<FormSectionDetails>(() => ({
  team: {
    type: "link",
    label: roster?.team?.name ?? "Team",
    path: `/teams/${roster?.teamId}/details`,
  },
  lastSynced: {
    type: "date",
    value: roster?.lastSynced ?? null,
  },
  modifiedBy: {
    type: "user",
    value: roster?.modifiedBy ?? "",
  },
  dateModified: {
    type: "date",
    value: roster?.dateModified ?? null,
  },
  createdBy: {
    type: "user",
    value: roster?.createdBy ?? "",
  },
  dateCreated: {
    type: "date",
    value: roster?.dateCreated ?? null,
  },
}));

const columns: TableColumn<AdminAthleteRosterResponse>[] = [
  {
    key: "athleteName",
    label: "Athlete",
    type: "slot",
  },
  {
    key: "athleteRosterNumber",
    label: "Roster Number",
    type: "slot",
  },
  {
    key: "shirtName",
    label: "Shirt Name",
    type: "slot",
  },
  {
    key: "isTracked",
    label: "Tracked by Provider",
    type: "bool",
  },
];

const athletesByTab = computed(() => {
  const athletes = roster?.athletesInRoster || [];

  const activeAthletes = athletes.filter(
    (athlete: AdminAthleteRosterResponse) =>
      !athlete.isArchived && athlete.isApproved,
  );
  const pendingAthletes = athletes.filter(
    (athlete: AdminAthleteRosterResponse) =>
      !athlete.isArchived && !athlete.isApproved,
  );
  const archivedAthletes = athletes.filter(
    (athlete: AdminAthleteRosterResponse) => athlete.isArchived,
  );

  return {
    active: activeAthletes,
    pending: pendingAthletes,
    archived: archivedAthletes,
  };
});

const rosterTabs = computed(() => {
  const counts = athletesByTab.value;

  return [
    {
      key: "active",
      title: "Active",
      count: counts.active.length,
    },
    {
      key: "pending",
      title: "Pending Approval",
      count: counts.pending.length,
    },
    {
      key: "archived",
      title: "Archived",
      count: counts.archived.length,
    },
  ];
});

const currentTabAthletes = computed(() => {
  return athletesByTab.value[activeTab.value] || [];
});

const { mutateAsync: updateRosterAthlete } = useUpdateRosterAthleteMutation();
const { mutateAsync: approveRosterAthlete } = useApproveRosterAthleteMutation();

const onRosterAthleteChanged = debounce(
  async (
    athleteId: string,
    playerNumber: string,
    shirtName: string | undefined,
  ) => {
    if (!rosterId.value || !athleteId || !playerNumber) return;
    try {
      await updateRosterAthlete({
        rosterId: rosterId.value,
        athleteId,
        athleteRosterNumber: parseInt(playerNumber),
        shirtName: shirtName,
      });
      toast.success("Roster athlete updated successfully");
    } catch (e) {
      toast.error(getErrorMessage(e)[0]);
    }
  },
  500,
);

const handleAthleteSaved = (
  athleteId: string,
  rosterNumber: string,
  shirtName: string | undefined,
) => {
  onRosterAthleteChanged(athleteId, rosterNumber, shirtName);
};

const handleApproveAthlete = async (athlete: AdminAthleteResponse) => {
  if (!rosterId.value || !athlete.id) return;

  await approveRosterAthlete({
    rosterId: rosterId.value,
    athleteId: athlete.id,
  });
  toast.success(`${athlete.shirtName} approved successfully`);
};
</script>

<template>
  <div>
    <CardVue>
      <FormSection
        title="Roster Details"
        :details-list="detailsList"
        :display-divider="false"
      >
        <div class="flex">
          <HorizontalTabs
            v-model="activeTab"
            :tabs="rosterTabs"
            value-key="key"
            class="w-full"
          />
          <BaseButton
            variant="primary"
            label="Add athlete"
            class="text-nowrap"
            @click="isAthleteCreationModalOpen = true"
          />
        </div>

        <TableVue
          :key="activeTab"
          :data="currentTabAthletes"
          :columns="columns"
        >
          <template #athleteName="{ item }">
            <RouterLinkVue :to="`/athletes/${item.athleteId}/details`">
              {{ item.athlete?.defaultDisplayName ?? item.athlete?.name }}
            </RouterLinkVue>
            <div
              v-if="item.athlete?.position"
              class="text-xs text-gray-500"
            >
              {{ item.athlete.position }}
            </div>
          </template>
          <template #athleteRosterNumber="{ item }">
            <InRowEditField
              :id="`roster-${item.id}`"
              :model-value="item.athleteRosterNumber?.toString() ?? ''"
              :name="`rosterNumber-${item.athlete?.id}`"
              input-type="number"
              @save="
                (value) =>
                  handleAthleteSaved(
                    item.athlete?.id ?? '',
                    value,
                    item?.shirtName ?? undefined,
                  )
              "
            />
          </template>

          <template #shirtName="{ item }">
            <InRowEditField
              :id="`shirtName-${item.id}`"
              input-type="text"
              name="shirtName"
              :model-value="item?.shirtName ?? ''"
              input-width="80"
              @save="
                (input) =>
                  handleAthleteSaved(
                    item.athlete?.id ?? '',
                    item.athleteRosterNumber?.toString() ?? '',
                    input,
                  )
              "
            />
          </template>

          <template #rowActions="{ item }">
            <div class="flex items-center gap-2">
              <BaseButton
                v-if="!item.isApproved && item.athlete && !item.isArchived"
                variant="primary"
                label="Approve"
                @click="handleApproveAthlete(item.athlete)"
              />
              <RosterAthleteActionMenu
                v-if="item.athlete && roster"
                :athlete-roster="item"
                :roster-id="roster?.id"
              />
            </div>
          </template>
        </TableVue>
      </FormSection>
    </CardVue>

    <AddAthleteToRosterModal
      v-if="roster"
      v-model:is-open="isAthleteCreationModalOpen"
      :roster="roster"
    />
  </div>
</template>
